import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import {
	Box,
	Button,
	Divider,
	FormControl,
	FormLabel,
	Heading,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Select,
	Stack,
	Table,
	Tbody,
	Td,
	Text,
	Textarea,
	Th,
	Thead,
	Tr,
	useDisclosure,
	useToast,
	VStack,
} from "@chakra-ui/react";

import { addActivityAsync } from "../../redux/user/userSlice";
import { addActivityVerifier } from "../../utils/verifier";
import ActivitiesTableRow from "../TableRows/ActivitiesTableRow";

export default function ActivitiesTab({ property }) {
	const { t } = useTranslation();
	const isArchived = property.archived;
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [error, setError] = useState({});
	const initialActivityInformation = {
		type: "",
		title: "",
		description: "",
	};
	const [activityInformation, setActivityInformation] = useState(initialActivityInformation);
	const toast = useToast();
	const dispatch = useDispatch();
	const addActivity = () => {
		const payload = { ...activityInformation, propertyId: property.id };
		console.log(payload);
		const errors = addActivityVerifier(activityInformation);
		if (Object.keys(errors).length > 0) {
			toast({
				title: "Error encountered",
				description: "Please fill all the fields",
				status: "error",
				duration: 9000,
				isClosable: true,
			});
			setError(errors);
			return;
		}
		setError({});
		dispatch(addActivityAsync(payload)).then((r) => {
			if (r.error?.message)
				toast({
					title: "Error encountered",
					description: r.error.message,
					status: "error",
					duration: 9000,
					isClosable: true,
				});
			else {
				toast({
					title: "Success!",
					description: "Activity added successfully",
					status: "success",
					duration: 2000,
					isClosable: true,
				});
				onClose();
			}
		});
	};
	const handleActivityInformationChange = (e) => {
		const { name, value } = e.target;
		setActivityInformation({ ...activityInformation, [name]: value });
	};
	const renderAddActivityModal = () => (
		<Modal isOpen={isOpen} onClose={onClose} size="lg">
			<ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
			<ModalContent mx={4}>
				<ModalHeader pb={0}>
					<Heading size="lg" fontWeight="bold" color="blue.600">
						{t("property.addActivity")}
					</Heading>
				</ModalHeader>
				<ModalCloseButton />
				<Divider mt={4} />
				<ModalBody py={6}>
					<VStack spacing={6} align="stretch">
						<FormControl isInvalid={error.title}>
							<FormLabel fontWeight="medium" color="gray.700">
								{t("table.title")}
							</FormLabel>
							<Input
								name="title"
								placeholder={t("property.enterTitle")}
								_placeholder={{ color: "gray.400" }}
								type="text"
								value={activityInformation.title}
								onChange={handleActivityInformationChange}
								size="md"
								borderRadius="md"
								focusBorderColor="blue.400"
							/>
						</FormControl>

						<FormControl isInvalid={error.type}>
							<FormLabel fontWeight="medium" color="gray.700">
								{t("table.type")}
							</FormLabel>
							<Select
								name="type"
								placeholder={t("property.selectType")}
								_placeholder={{ color: "gray.400" }}
								value={activityInformation.type}
								onChange={handleActivityInformationChange}
								size="md"
								borderRadius="md"
								focusBorderColor="blue.400"
							>
								<option value="insurance">{t("activityTypes.insurance")}</option>
								<option value="administrationContract">{t("activityTypes.administrationContract")}</option>
								<option value="lease">{t("activityTypes.lease")}</option>
								<option value="email">{t("activityTypes.email")}</option>
								<option value="whatsapp">{t("activityTypes.whatsapp")}</option>
								<option value="conversation">{t("activityTypes.conversation")}</option>
								<option value="other">{t("activityTypes.other")}</option>
							</Select>
						</FormControl>

						<FormControl isInvalid={error.description}>
							<FormLabel fontWeight="medium" color="gray.700">
								{t("table.description")}
							</FormLabel>
							<Textarea
								name="description"
								placeholder={t("property.enterDescription")}
								_placeholder={{ color: "gray.400" }}
								value={activityInformation.description}
								onChange={handleActivityInformationChange}
								size="md"
								borderRadius="md"
								focusBorderColor="blue.400"
								rows={4}
								resize="vertical"
							/>
						</FormControl>
					</VStack>
				</ModalBody>
				<Divider />
				<ModalFooter pt={4}>
					<Button variant="ghost" onClick={onClose} mr={3} _hover={{ bg: "gray.100" }}>
						{t("misc.cancel")}
					</Button>
					<Button colorScheme="blue" onClick={addActivity} px={8} _hover={{ transform: "translateY(-1px)" }} transition="all 0.2s">
						{t("misc.save")}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
	return (
		<Stack>
			<Box>
				<Button colorScheme={"blue"} onClick={onOpen} isDisabled={isArchived}>
					{t("property.addActivity")}
				</Button>
				{renderAddActivityModal()}
			</Box>
			<Box overflowX="auto">
				<Table variant="simple" size="sm">
					<Thead>
						<Tr my=".8rem" pl="0px">
							<Th>{t("table.addedDate")}</Th>
							<Th>{t("table.title")}</Th>
							<Th>{t("table.type")}</Th>
							<Th>{t("table.description")}</Th>
							<Th></Th>
						</Tr>
					</Thead>
					<Tbody>
						{property.activities?.map((a, index) => (
							<ActivitiesTableRow key={index} activity={a} property={property} />
						))}
						{property.activities?.length === 0 && (
							<Tr>
								<Td colSpan={5}>
									<Text>{t("table.noData")}</Text>
								</Td>
							</Tr>
						)}
					</Tbody>
				</Table>
			</Box>
		</Stack>
	);
}
